import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Eye, Cookie, Database, Lock, Users, Mail, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import SEOHead from './SEOHead';

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedPath();

  const handleBackToHome = () => {
    navigate(getLocalizedPath('/'));
  };

  const sections = [
    { id: 'informationCollected', icon: Database },
    { id: 'howWeUse', icon: Settings },
    { id: 'cookies', icon: Cookie },
    { id: 'thirdParty', icon: Users },
    { id: 'dataSecurity', icon: Lock },
    { id: 'yourRights', icon: Shield },
    { id: 'childrenPrivacy', icon: Users },
    { id: 'changes', icon: Eye },
    { id: 'contact', icon: Mail }
  ];

  return (
    <div className="space-y-6">
      <SEOHead
        title={t('privacyPolicy.pageTitle')}
        description={t('privacyPolicy.pageDescription')}
      />

      {/* Back Button */}
      <button
        onClick={handleBackToHome}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common.backToAllConverters')}
      </button>

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-4">
          <h1 className="text-xl font-semibold text-white">
            {t('privacyPolicy.pageTitle')}
          </h1>
        </div>
        <div className="p-4">
          <p className="text-gray-600">
            {t('privacyPolicy.lastUpdated')}: {t('privacyPolicy.lastUpdatedDate')}
          </p>
          <p className="text-gray-600 mt-3">
            {t('privacyPolicy.introduction')}
          </p>
        </div>
      </div>

      {/* Privacy Sections */}
      {sections.map((section, index) => {
        const IconComponent = section.icon;
        return (
          <div
            key={section.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-4 py-3">
              <div className="flex items-center gap-2">
                <IconComponent className="h-5 w-5 text-white" />
                <h2 className="text-lg font-semibold text-white">
                  {index + 1}. {t(`privacyPolicy.sections.${section.id}.title`)}
                </h2>
              </div>
            </div>
            <div className="p-4">
              <p className="text-gray-600 text-sm whitespace-pre-line">
                {t(`privacyPolicy.sections.${section.id}.content`)}
              </p>
              {section.id === 'cookies' && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>{t('privacyPolicy.cookieNote')}:</strong> {t('privacyPolicy.cookieNoteText')}
                  </p>
                </div>
              )}
              {section.id === 'contact' && (
                <div className="mt-3">
                  <a
                    href="mailto:privacy@metricconverters.com"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    <Mail className="h-4 w-4" />
                    privacy@metricconverters.com
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Summary Box */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#4a7c9b] to-[#5a8caa] px-4 py-3">
          <h2 className="text-lg font-semibold text-white">
            {t('privacyPolicy.summaryTitle')}
          </h2>
        </div>
        <div className="p-4">
          <ul className="space-y-2">
            {t('privacyPolicy.summaryPoints', { returnObjects: true }).map((point, index) => (
              <li key={index} className="flex items-start gap-2 text-gray-600 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4a7c9b] mt-1.5 flex-shrink-0"></span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
